import { lazy, Suspense, type ReactNode } from "react";
import HomePage from "../pages/HomePage";
import LogInPage from "../pages/LogInPage";
import CancelPage from "../pages/CancelPage";
import SuccessPage from "../pages/SuccessPage";
import LandingPage from "../pages/LandingPage";
import AdsPage from "../pages/AdsPage/AdsPage";
import AdsProductPage from "../pages/AdsPage/AdsProductPage";
import SettingsPage from "../pages/SettingsPage";
import WebsitesPage from "../pages/WebsitesPage";
import StartSurvery from "../forms/StartSurvery";
import { PlansPage } from "../pages/Plans/PlansPage";
import SalesPage from "../pages/SalesPage/SalesPage";
import LandingOfferPage from "../pages/LandingOfferPage";
import SentryTestPage from "../components/SentryTestPage";
import UserPreferencesPage from "../pages/UserPreferences";
import UserProfilePage from "../pages/UserProfilePage";
import ProductsPage from "../pages/productPage/productPage";
import { PaymentResultPage } from "../pages/PaymentResultPage";
import AnalyticsPage from "../pages/AnaliticsPage/AnalyticsPage";
import OnboardingFakePage from "../pages/FunnelCreationFakePage";
import AbTestingPage from "../components/ab-testing/AbTestingPage";
import WebsitesBuilderIFramePage from "../pages/WebsiteBuilderIframePage";
import { MembershipPage } from "../pages/membershipsPage/MembershipsPage";
import { IntegrationsPage } from "../pages/Integrations/IntegrationsPage";
import ChatPage from "../pages/ChatPage/ChatPage";
import CustomerKanbanPage from "../pages/CustomerKanbanPage";

import { OrderDetailsPage } from "../pages/OrderDetailsPage/OrderDetailsPage";
import { AbandonedCartDetailPage } from "../pages/AbandonedCartDetailPage/AbandonedCartDetailPage";
import { OnboardingLayout } from "../components/onboardingNew/OnboardingLayout";
import { OnboardingLayoutHome } from "../components/onboardingHome/OnboardingLayoutHome";
import OnboardingGuard from "./OnboardingGuard";
import { OnboardingLayoutHomeDemo } from "../components/onboardingHome1/OnboardingLayoutHomeDemo";
import HyphensPage from "@/pages/hyphens/HyphensPage";
import { SwitchGuestPage } from "@/pages/GuestPage/SwitchGuest.page";
import PrivacyPolicyPage from "../pages/PrivacyPolicyPage";
import MetaCallbackPage from "../pages/MetaCallbackPage";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import ChatAccessRoute from "./ChatAccessRoute";
import FinanceAccessRoute from "./FinanceAccessRoute";

const FinancePage = lazy(() => import("../pages/FinancePage"));

const financeRouteFallback = (
  <div className="flex min-h-screen items-center justify-center bg-[#f5f6fa] text-sm font-medium text-gray-500">
    Cargando Fluxi Finance...
  </div>
);

const financeRoute = (children: ReactNode) => (
  <FinanceAccessRoute>
    <Suspense fallback={financeRouteFallback}>{children}</Suspense>
  </FinanceAccessRoute>
);

// Legacy onboarding routes are intentionally disconnected from the normal app.
// Keep the code reachable only in local dev for forensic comparison:
// VITE_ENABLE_LEGACY_ONBOARDING=true npm run dev
const enableLegacyOnboardingRoutes =
  import.meta.env.DEV && import.meta.env.VITE_ENABLE_LEGACY_ONBOARDING === "true";

const legacyFunnelOnboardingElement = enableLegacyOnboardingRoutes
  ? <OnboardingFakePage />
  : <Navigate to="/onboarding" replace />;

const legacyHomeOnboardingElement = enableLegacyOnboardingRoutes
  ? <OnboardingLayoutHomeDemo />
  : <Navigate to="/onboarding/home" replace />;

const mapLegacyFunnelView = (tab: string | null) => {
  if (tab === "marketing") return "marketing";
  if (tab === "logistics") return "logistics";
  if (tab === "cpa") return "cpa";
  return "price";
};

function LegacyFinanceFunnelRedirect() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const params = new URLSearchParams({ tab: "funnels" });
  if (id) params.set("funnel", id);
  params.set("view", mapLegacyFunnelView(searchParams.get("tab")));

  return <Navigate to={`/finance?${params.toString()}`} replace />;
}

export const protectedRoutes = [
  { path: "/ads", element: <AdsPage /> },
  { path: "/ads/product/:productId", element: <AdsProductPage /> },
  { path: "/hyphens", element: <HyphensPage /> },
  { path: "/home", element: <HomePage /> },
  { path: "/plans", element: <PlansPage /> },
  { path: "/orders", element: <SalesPage /> },
  { path: "/survey", element: <StartSurvery /> },
  { path: "/funnels", element: <WebsitesPage /> },
  { path: "/finance", element: financeRoute(<FinancePage />) },
  { path: "/finance/funnels/:id", element: financeRoute(<LegacyFinanceFunnelRedirect />) },
  { path: "/products", element: <ProductsPage /> },
  { path: "/settings", element: <SettingsPage /> },
  { path: "/ab-testing", element: <AbTestingPage /> },
  { path: "/membership", element: <MembershipPage /> },
  { path: "/sentry-test", element: <SentryTestPage /> },
  { path: "/onboarding", element: <OnboardingGuard><OnboardingLayout /></OnboardingGuard> },
  { path: "/integrations", element: <IntegrationsPage /> },
  { path: "/chat", element: <ChatAccessRoute><ChatPage /></ChatAccessRoute> },
  { path: "/chat/kanban/:phone", element: <ChatAccessRoute><CustomerKanbanPage /></ChatAccessRoute> },
  { path: "/kanban", element: <ChatAccessRoute><Navigate to="/chat?tab=confirmaciones" replace /></ChatAccessRoute> },
  { path: "/onboarding1", element: legacyFunnelOnboardingElement },
  { path: "/order/:hash_id", element: <OrderDetailsPage /> },
  { path: "/abandoned-cart/:session_id", element: <AbandonedCartDetailPage /> },
  { path: "/analytics/:website_id", element: <AnalyticsPage /> },
  { path: "/onboarding/home", element: <OnboardingGuard><OnboardingLayoutHome /></OnboardingGuard> },
  { path: "/user/preferences", element: <UserPreferencesPage /> },
  { path: "/profile", element: <UserProfilePage /> },
  { path: "/onboarding1/home1", element: legacyHomeOnboardingElement },
  { path: "/complete-email-setup", element: <SwitchGuestPage /> },
];

export const publicRoutes = [
  { path: "/", element: <LandingPage /> },
  { path: "/login", element: <LogInPage /> },
  { path: "/login/offer", element: <LandingOfferPage /> },
  { path: "/payment/:status", element: <PaymentResultPage /> },
  { path: "/payment-subscription/cancel", element: <CancelPage /> },
  { path: "/payment-subscription/success", element: <SuccessPage /> },
  { path: "/website/builder/guest", element: <WebsitesBuilderIFramePage /> },
  { path: "/privacy-policy", element: <PrivacyPolicyPage /> },
  { path: "/meta", element: <MetaCallbackPage /> },
  { path: "/finance/meta", element: <MetaCallbackPage /> },
];
